import type { ReactNode } from 'react';

import type { OrgFormValues } from '#/components/Form/Fields/Org/getOrgFields';
import type { I18nFn } from '#/i18n/types/I18nFn';
import { ApiOrgType } from '#/modules/listing/orgs/api/types/ApiOrgType';

import { getOrgDetailFields } from './getOrgDetailFields';

export function getEditFields(
  i18n: I18nFn,
  values: OrgFormValues,
  showVatNumber: boolean,
  orgType: ApiOrgType,
  isPending: boolean,
  donateEnabled: boolean | null,
  membershipCallout: ReactNode | null,
) {
  const orgTypeValue = values.orgType ?? orgType ?? ApiOrgType.NONPROFIT;

  return [
    {
      title: i18n('Basic Info'),
      sections: [
        {
          rows: [
            {
              fields: ['name'],
            },
            {
              fields: ['logo'],
            },
          ],
        },
      ],
    },
    {
      title: i18n('Organization Details'),
      sections: [
        {
          rows: [
            {
              fields: ['locale'],
            },
            ...(showVatNumber
              ? [
                  {
                    fields: ['vatNumber'],
                  },
                ]
              : []),
            {
              fields: ['linkedinId'],
            },
            ![
              ApiOrgType.CONSULTANT,
              ApiOrgType.RECRUITER,
              ApiOrgType.CORPORATION,
              ApiOrgType.COMMUNITY,
            ].includes(orgTypeValue)
              ? {
                  fields: ['governmentId'],
                }
              : {
                  fields: [],
                },
            ...(isPending
              ? getOrgDetailFields({
                  orgType: orgTypeValue,
                })
              : [
                  {
                    fields: [],
                  },
                ]),
          ],
        },
      ],
    },
    {
      title: i18n('Additional Details'),
      sections: [
        {
          rows: [
            ...(orgType === ApiOrgType.NONPROFIT
              ? [
                  {
                    fields: ['mission'],
                  },
                ]
              : []),
            {
              fields: ['description'],
            },
            ...(orgType === ApiOrgType.CORPORATION
              ? []
              : [
                  {
                    fields: ['areasOfFocus'],
                  },
                ]),
          ],
        },
      ],
    },
    ...([ApiOrgType.NONPROFIT, ApiOrgType.GOVERNMENT].includes(orgType)
      ? [
          {
            title: i18n('Accept Donations'),
            preTitle: i18n('Members Only'),
            disabled: !donateEnabled,
            beforeSection: membershipCallout,
            glowUp: donateEnabled,
            sections: [
              {
                rows: [
                  {
                    fields: ['donationLink'],
                  },
                ],
              },
            ],
          },
        ]
      : []),
    {
      title: i18n('Contact'),
      sections: [
        {
          rows: [
            {
              fields: ['links'],
            },
            {
              fields: ['email'],
            },
            {
              fields: ['phone'],
            },
          ],
        },
      ],
    },
    {
      title: i18n('Location'),
      sections: [
        {
          rows: [
            {
              fields: ['locationType'],
            },
            {
              fields: ['address'],
            },
          ],
        },
      ],
    },
    {
      title: i18n('Media Gallery'),
      sections: [
        {
          rows: [
            {
              fields: ['banner'],
            },
            {
              fields: ['slideshowItems'],
            },
          ],
        },
      ],
    },
  ];
}
